import { snackbarUtils } from '@ahamove/web-components/lib/SnackbarProvider';
import React from 'react';
import { getGeocodeReverseFn } from '@/api/place/getGeocodeReverseFn';
import type { Address, LatLng } from '@/api/place/types';
import { useCurrentLocationStore } from '@/global-stores/useCurrentLocationStore';
import { GET_CURRENT_LOCATION_MAX_AGE } from '@/lib/constants';
import { session } from '@/lib/storage';
import { checkGeolocationPermission, getCurrentPosition } from '@/lib/utils';

export type UseCurrentLocationProps = {
  currentLocation: Address | null;
  loading: boolean;
  isError: boolean;
  getCurrentLocation: () => Promise<void>;
  // getCurrentLocation: () => Promise<(() => void) | undefined>;
};

const useCurrentLocation = (): UseCurrentLocationProps => {
  const [isError, setIsError] = React.useState<boolean>(false);

  const {
    currentAddress,
    loading,
    coords,
    setCoords,
    setDetectingLocation,
    setCurrentAddress,
  } = useCurrentLocationStore();

  const getCurrentLocation = async () => {
    const isAllowGetLocation = await checkGeolocationPermission();

    if (!isAllowGetLocation) {
      // snackbarUtils.error(t('booking:location.detect_current_location_error'));
      setIsError(true);
      setDetectingLocation(false);
      return;
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setIsError(true);
      snackbarUtils.error(
        'Phương thức hiện tại không được hỗ trợ, vui lòng thử lại sau.'
      );
      setDetectingLocation(false);
      return;
    }

    // Check if previous location data is available in cache
    const cachedLocation = session.getItem('cachedLocation');
    if (cachedLocation) {
      const { lat, lng } = JSON.parse(cachedLocation);
      setCoords({ ...coords, lat, lng });
      return;
    }

    setDetectingLocation(true);
    getCurrentPosition()
      .then((latLng) => {
        const { lat, lng } = latLng;

        if (!coords || (coords && coords.lat !== lat && coords.lng !== lng)) {
          setCoords({ ...coords, lat, lng });
          session.setItem('cachedLocation', JSON.stringify({ lat, lng }));
        }
      })
      .catch((err) => {
        setDetectingLocation(false);
        setIsError(true);
      });
  };

  const locationSearchFn = React.useCallback((latLng: LatLng | null) => {
    if (loading && latLng?.lat && latLng?.lng) {
      // setDetectingLocation(true);
      getGeocodeReverseFn(latLng)
        .then((res) => {
          if (res) {
            setCurrentAddress(res);
            setDetectingLocation(false);
            setIsError(false);
          }
        })
        .catch(() => {
          setDetectingLocation(false);
        });
    } else {
      setDetectingLocation(false);
    }
  }, []);

  const detect = React.useCallback(async () => {
    setDetectingLocation(true);

    const isAllowGetLocation = await checkGeolocationPermission();
    if (!isAllowGetLocation) {
      // snackbarUtils.error(t('booking:location.detect_current_location_error'));
      setIsError(true);
      setDetectingLocation(false);
      return;
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setIsError(true);
      snackbarUtils.error(
        'Phương thức hiện tại không được hỗ trợ, vui lòng thử lại sau.'
      );
      setDetectingLocation(false);
      return;
    }

    // Check if previous location data is available in cache
    const cachedLocation = session.getItem('cachedLocation');
    if (cachedLocation) {
      const { lat, lng } = JSON.parse(cachedLocation);
      setCoords({ ...coords, lat, lng });
    }

    // Request current location data
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;

        if (!coords || (coords && coords.lat !== lat && coords.lng !== lng)) {
          setCoords({ ...coords, lat, lng });
          session.setItem('cachedLocation', JSON.stringify({ lat, lng }));
        }
      },
      () => {
        setDetectingLocation(false);
        setIsError(true);
        // if (
        //   err &&
        //   err.code === 1 &&
        //   err.message === 'User denied Geolocation'
        // ) {
        //   snackbarUtils.error(
        //     t('booking:location.detect_current_location_error')
        //   );
        // } else {
        //   snackbarUtils.error(err.message);
        // }
      },
      {
        enableHighAccuracy: true,
        maximumAge: GET_CURRENT_LOCATION_MAX_AGE,
      }
    );

    return () => {
      setDetectingLocation(false);
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  React.useEffect(() => {
    getCurrentLocation();
  }, []);

  // React.useEffect(() => {
  //   if (!currentAddress) {
  //     getCurrentLocation();
  //   }
  // }, [currentAddress]);

  React.useEffect(() => {
    if (!currentAddress) {
      coords && locationSearchFn(coords);
    } else {
      setDetectingLocation(false);
    }
  }, [coords, currentAddress]);

  return {
    currentLocation: currentAddress,
    loading,
    isError,
    getCurrentLocation,
  };
};

export default useCurrentLocation;
